import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PortalShell } from "@/pages/portal/PortalShell";
import { ScoreRing } from "@/components/portal/ScoreRing";
import { Plus, ArrowRight, FolderKanban, CreditCard, FileWarning, CheckCircle2, Check, X, Gift, Copy, MessageCircle } from "lucide-react";

const AI_STATUS_LABEL = {
  none: "Draft",
  analyzing: "AI Analysis in progress...",
  ready: "Analysis ready",
  error: "Analysis failed — retry",
};

export default function FounderDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [data, setData] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [claim, setClaim] = useState(null);
  const [claimTarget, setClaimTarget] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [referral, setReferral] = useState(null);

  useEffect(() => {
    if (!data) return;
    const sid = sessionStorage.getItem("ll_claim_service");
    if (!sid) return;
    api.get("/portal/services")
      .then((r) => {
        const sv = r.data.find((x) => x.id === sid);
        if (sv) {
          setClaim(sv);
          if (data.startups.length) setClaimTarget(data.startups[0].id);
        } else {
          sessionStorage.removeItem("ll_claim_service");
        }
      })
      .catch(() => {});
  }, [data]);

  const claimServiceNow = async () => {
    if (!claimTarget || !claim) return;
    setClaiming(true);
    try {
      await api.post("/portal/orders", { startup_id: claimTarget, service_id: claim.id });
      sessionStorage.removeItem("ll_claim_service");
      toast.success(`${claim.name} added to your plan — complete payment to activate`);
      navigate(`/dashboard/startup/${claimTarget}`);
    } catch (err) {
      sessionStorage.removeItem("ll_claim_service");
      setClaim(null);
      toast.error(formatApiError(err));
    } finally {
      setClaiming(false);
    }
  };

  useEffect(() => {
    api.get("/portal/auth/me")
      .then((r) => {
        setUser(r.data);
        if (r.data.role === "specialist") {
          navigate("/specialist");
          return;
        }
        api.get("/portal/dashboard").then((res) => setData(res.data)).catch(() => setData({ startups: [], stats: {} }));
        api.get("/portal/invitations").then((res) => setInvitations(res.data)).catch(() => {});
        api.get("/portal/referrals").then((res) => setReferral(res.data)).catch(() => {});
      })
      .catch(() => navigate("/portal"));
  }, [navigate]);

  const respondInvite = async (id, action) => {
    try {
      await api.post(`/portal/invitations/${id}/${action}`);
      toast.success(action === "accept" ? "Invitation accepted — the startup is now on your dashboard" : "Invitation declined");
      setInvitations((list) => list.filter((i) => i.id !== id));
      if (action === "accept") {
        api.get("/portal/dashboard").then((res) => setData(res.data)).catch(() => {});
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copyReferral = async () => {
    const link = `${window.location.origin}/portal?ref=${referral?.code}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Referral link copied — share it with other founders");
    } catch {
      toast.error("Copy failed — please copy the link manually");
    }
  };

  if (!user || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="dashboard-loading">
        <div className="w-10 h-10 rounded-full border-2 border-[#FF8800] border-t-transparent animate-spin" />
      </div>
    );
  }

  const stats = data.stats || {};
  const bestStartup = data.startups.find((s) => s.analysis?.score) || data.startups[0];

  return (
    <PortalShell user={user} title="Startup Command Center">
      <div data-testid="founder-dashboard">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-white" data-testid="dashboard-welcome">
            Welcome, <span className="text-gradient-saffron">{user.name}</span>
          </h1>
          <p className="mt-1.5 text-sm text-slate-400">Your startup command center — everything from idea to launch in one place.</p>
        </motion.div>

        {invitations.length > 0 && (
          <div className="glass rounded-xl p-5 mt-8 border border-[#FF8800]/30" data-testid="invitations-banner">
            <p className="font-mono-tech text-[10px] tracking-[0.3em] text-[#FF8800] uppercase mb-3">Team invitations</p>
            <div className="space-y-2.5">
              {invitations.map((inv) => (
                <div key={inv.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-lg border border-white/10 px-4 py-3" data-testid={`invitation-${inv.id}`}>
                  <p className="text-sm text-slate-200">
                    <span className="text-white font-medium">{inv.invited_by}</span> invited you to <span className="text-[#FF8800]">{inv.startup_name}</span> as <span className="uppercase text-xs font-mono-tech">{inv.role}</span>
                  </p>
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" data-testid={`invitation-accept-${inv.id}`} onClick={() => respondInvite(inv.id, "accept")} className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold">
                      <Check className="w-3.5 h-3.5 mr-1" /> Accept
                    </Button>
                    <Button size="sm" variant="outline" data-testid={`invitation-decline-${inv.id}`} onClick={() => respondInvite(inv.id, "decline")} className="border-white/15 text-slate-300">
                      <X className="w-3.5 h-3.5 mr-1" /> Decline
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {claim && (
          <div className="glass rounded-xl p-5 mt-8 border border-[#00E676]/30" data-testid="claim-banner">
            <p className="font-mono-tech text-[10px] tracking-[0.3em] text-[#00E676] uppercase mb-2">Your selected service</p>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <p className="font-display font-bold text-white">{claim.name}</p>
                <p className="text-xs text-slate-400 mt-0.5">₹{Number(claim.price).toLocaleString("en-IN")} + {claim.gst_percent}% GST · {claim.specialist_category}</p>
              </div>
              {data.startups.length > 0 ? (
                <div className="flex items-center gap-2.5 shrink-0 flex-wrap">
                  {data.startups.length > 1 && (
                    <select data-testid="claim-startup-select" value={claimTarget} onChange={(e) => setClaimTarget(e.target.value)} className="bg-[#06080E] border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200">
                      {data.startups.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  )}
                  <Button onClick={claimServiceNow} disabled={claiming || !claimTarget} data-testid="claim-add-button" className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold">
                    {claiming ? "Adding..." : "Add to My Plan"}
                  </Button>
                </div>
              ) : (
                <Link to="/dashboard/new" data-testid="claim-create-startup" className="shrink-0">
                  <Button className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold">
                    Create My Startup First <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                </Link>
              )}
            </div>
            {data.startups.length === 0 && (
              <p className="text-[11px] text-slate-500 mt-2.5">Your selected service is saved — it will be added to your plan right after you submit your startup.</p>
            )}
          </div>
        )}

        {referral && (
          <div className="glass rounded-xl p-5 mt-8 border border-[#00B0FF]/25" data-testid="referral-card">
            <p className="font-mono-tech text-[10px] tracking-[0.3em] text-[#00B0FF] uppercase mb-2 flex items-center gap-2">
              <Gift className="w-3.5 h-3.5" /> Refer & Grow
            </p>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm text-slate-300">Share LaunchLab with other founders — every signup through your link is tracked here.</p>
                <p className="text-xs font-mono-tech text-slate-500 mt-1.5 truncate" data-testid="referral-link">{`${window.location.origin}/portal?ref=${referral.code}`}</p>
              </div>
              <div className="flex items-center gap-5 shrink-0">
                <div className="text-center">
                  <p className="font-display font-bold text-white">{referral.referred_count}</p>
                  <p className="text-[10px] font-mono-tech text-slate-500 uppercase">Joined</p>
                </div>
                <div className="text-center">
                  <p className="font-display font-bold text-[#00E676]">{referral.referred_paid}</p>
                  <p className="text-[10px] font-mono-tech text-slate-500 uppercase">Paid</p>
                </div>
                <Button variant="outline" size="sm" onClick={copyReferral} data-testid="referral-copy" className="border-white/15 text-slate-300 hover:bg-white/5">
                  <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy Link
                </Button>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`I'm building my startup with LaunchLab — free AI idea validation plus expert help for registrations, licensing and launch. Try it here: ${window.location.origin}/portal?ref=${referral.code}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="referral-whatsapp"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-[#25D366]/15 border border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/25 transition-colors"
                >
                  <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Stats row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8" data-testid="dashboard-stats">
          {[
            { icon: FolderKanban, label: "My Startups", value: stats.startups ?? 0, color: "#FF8800" },
            { icon: CheckCircle2, label: "Active Services", value: stats.active_services ?? 0, color: "#00B0FF" },
            { icon: FileWarning, label: "Pending Documents", value: stats.pending_documents ?? 0, color: "#00E676" },
            { icon: CreditCard, label: "Pending Payments", value: stats.pending_payments ?? 0, color: "#FF8800" },
          ].map((c, i) => (
            <motion.div
              key={c.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.07 }}
              className="glass rounded-xl p-5"
              data-testid={`dashboard-stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3" style={{ background: `${c.color}14`, border: `1px solid ${c.color}33` }}>
                <c.icon className="w-4 h-4" style={{ color: c.color }} />
              </div>
              <p className="font-display font-extrabold text-2xl text-white">{c.value}</p>
              <p className="text-[11px] font-mono-tech tracking-wider text-slate-500 uppercase mt-0.5">{c.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Startups */}
        <div className="flex items-center justify-between mt-12 mb-6">
          <h2 className="font-display text-xl font-bold text-white">My Startups</h2>
          <Link to="/dashboard/new" data-testid="dashboard-create-startup">
            <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold text-sm transition-transform hover:scale-105">
              <Plus className="w-4 h-4" /> Create New Startup
            </span>
          </Link>
        </div>

        {data.startups.length === 0 ? (
          <div className="glass rounded-2xl p-14 text-center" data-testid="dashboard-empty">
            <p className="font-display text-xl font-bold text-white mb-2">Start your first venture</p>
            <p className="text-sm text-slate-400 mb-6 max-w-md mx-auto">
              Register your startup idea, let LaunchLab AI validate it, and get a personalized launch roadmap.
            </p>
            <Link to="/dashboard/new" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold text-sm">
              <Plus className="w-4 h-4" /> Create New Startup
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5" data-testid="dashboard-startups">
            {data.startups.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
              >
                <Link
                  to={`/dashboard/startup/${s.id}`}
                  data-testid={`startup-card-${s.id}`}
                  className="card-beam group block glass rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[#FF8800]/40"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-display text-lg font-bold text-white truncate group-hover:text-[#FF8800] transition-colors">{s.name}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {s.answers?.industry || "Industry not set"} · {s.answers?.current_stage || s.status}
                        {s.member_role && s.member_role !== "owner" && (
                          <span className="ml-2 text-[10px] font-mono-tech px-2 py-0.5 rounded-full border border-[#00B0FF]/40 text-[#00B0FF]" data-testid={`startup-role-${s.id}`}>{s.member_role.toUpperCase()}</span>
                        )}
                      </p>
                      <p className={`text-[11px] font-mono-tech mt-2 ${s.ai_status === "ready" ? "text-[#00E676]" : s.ai_status === "analyzing" ? "text-[#00B0FF] animate-pulse" : "text-slate-500"}`}>
                        {AI_STATUS_LABEL[s.ai_status] || s.ai_status}
                      </p>
                    </div>
                    {s.analysis?.score != null && <ScoreRing score={s.analysis.score} size={72} testid={`startup-score-${s.id}`} />}
                  </div>
                  <div className="mt-5">
                    <div className="flex justify-between text-[11px] mb-1.5">
                      <span className="text-slate-400 font-mono-tech tracking-wider uppercase">Launch readiness</span>
                      <span className="text-slate-300 font-mono-tech">{s.readiness}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-[#FF8800] to-[#00E676]" style={{ width: `${s.readiness}%` }} />
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-slate-500">{s.order_count} service{s.order_count === 1 ? "" : "s"} in plan</span>
                    <span className="inline-flex items-center gap-1.5 text-xs text-[#FF8800] font-medium">
                      Open command center <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}

        {bestStartup?.analysis && (
          <p className="mt-10 text-[11px] font-mono-tech tracking-wider text-slate-600 uppercase text-center" data-testid="dashboard-ai-disclaimer">
            AI-assisted preliminary assessment — not investment, legal or professional advice.
          </p>
        )}
      </div>
    </PortalShell>
  );
}
